import imageCompression from "browser-image-compression";
import exifr from "exifr";

// 三檔規格（webp，瀏覽器壓縮）
const PROFILES = {
  thumb: { maxWidthOrHeight: 600, maxSizeMB: 0.12, fileType: "image/webp", initialQuality: 0.78 },
  medium: { maxWidthOrHeight: 1920, maxSizeMB: 0.6, fileType: "image/webp", initialQuality: 0.82 },
};

// HEIC 偵測（iPhone 預設拍照格式，瀏覽器原生不能 decode）
function isHeic(file) {
  const mime = (file.type || "").toLowerCase();
  if (mime === "image/heic" || mime === "image/heif") return true;
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
}

let _heicToPromise = null;
function loadHeicTo() {
  if (!_heicToPromise) {
    _heicToPromise = import("heic-to/csp").then((m) => m.heicTo || m.default || m);
  }
  return _heicToPromise;
}

async function decodeHeicIfNeeded(file) {
  if (!isHeic(file)) return file;
  let heicTo;
  try {
    heicTo = await loadHeicTo();
  } catch (e) {
    throw new Error(`heic-to 載入失敗: ${e.message || e}`);
  }
  let blob;
  try {
    /*
      heic-to tracks current libheif builds and compiles with libde265,
      the HEVC/H.265 decoder libheif uses for iPhone HEIC photos.
      The /csp entry also avoids unsafe-eval in production CSP setups.
    */
    blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.92 });
  } catch (e) {
    throw new Error(`HEIC 解碼失敗 (${e.message || e})`);
  }
  const out = Array.isArray(blob) ? blob[0] : blob;
  const newName = file.name.replace(/\.(heic|heif)$/i, "") + ".jpg";
  return new File([out], newName, { type: "image/jpeg" });
}

// 讀 EXIF 拍攝時間（DateTimeOriginal）— exifr 可從原 HEIC 直接讀
export async function readExifDate(file) {
  try {
    const tags = await exifr.parse(file, ["DateTimeOriginal", "CreateDate", "DateTime"]);
    if (!tags) return null;
    const d = tags.DateTimeOriginal || tags.CreateDate || tags.DateTime;
    if (!d) return null;
    const date = d instanceof Date ? d : new Date(d);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

// 把單一原圖（含 HEIC）壓出 thumb + medium，回傳 {thumb, medium, original, exifDate}
export async function processImage(file, onProgress) {
  // 1) EXIF 從原檔讀（即使 HEIC exifr 也能解）
  const exifDate = await readExifDate(file);

  // 2) HEIC → JPEG 解碼（其他格式直接跳過）
  let working;
  try {
    working = await decodeHeicIfNeeded(file);
    onProgress?.("decoded", 100);
  } catch (e) {
    throw new Error(e.message || "HEIC 解碼失敗");
  }

  // 3) 壓縮成 thumb + medium
  let thumb, medium;
  try {
    [thumb, medium] = await Promise.all([
      imageCompression(working, {
        ...PROFILES.thumb,
        useWebWorker: true,
        onProgress: (p) => onProgress?.("thumb", p),
      }),
      imageCompression(working, {
        ...PROFILES.medium,
        useWebWorker: true,
        onProgress: (p) => onProgress?.("medium", p),
      }),
    ]);
  } catch (e) {
    const msg = e?.message || e?.toString?.() || JSON.stringify(e);
    throw new Error(`壓縮失敗: ${msg || "unknown"}`);
  }

  const baseName = working.name.replace(/\.[^.]+$/, "") || "photo";
  const thumbFile = new File([thumb], `${baseName}.webp`, { type: "image/webp" });
  const mediumFile = new File([medium], `${baseName}.webp`, { type: "image/webp" });

  return {
    thumb: thumbFile,
    medium: mediumFile,
    original: working, // HEIC 已轉 JPEG，JPG/PNG 維持原檔
    exifDate,
  };
}
